// Global Instructions Rule Applied!
//
// Campaign dispatch status strip for the email compose form (Full.tsx):
// status Tag + "N sent / N failed of M" + timestamps, plus the dispatch
// alerts added after the 2026-09-14 PROD incident, when a send died mid-run
// and nobody could see it for 42 minutes:
//
//   * Stalled (queued / sending with no recipient progress for the cfg
//     threshold, email/dispatch_stall) → red Alert telling the operator what
//     to do: Halt a stalled send, Retry a queued campaign whose dispatch never
//     started.
//   * Halted → gold Alert: who halted it, when, and how many recipients wait.
//   * Unconfirmed recipients on a halted / failed campaign (a run stopped
//     before recording whether Postmark accepted them) → the alert points at
//     "Check Postmark & re-queue" before anything is sent again. A mailbox
//     campaign (viaMailbox) is checked against the sender's own mailbox
//     instead: "Check Sent Items & re-queue" (UNCONFIRMED_CHECK holds the
//     wording of both, for this strip and for the form's button and modal).
//
// Progress is read from view__email_campaign_detail; seconds_since_progress
// is computed against the DB clock, so a skewed browser clock can't fake or
// hide a stall. Full.tsx refetches every 5 s while the campaign is
// queued / sending / halted.

import React from 'react'
import { Alert, Space, Tag, Typography } from 'antd'

const { Text } = Typography

/** Used when cfg email/dispatch_stall is unseeded (the RPC returns null). */
export const DEFAULT_STALL_WARNING_SECONDS = 90

export interface DispatchProgress {
  secondsSinceProgress: number | null
  pendingCount: number
  unconfirmedCount: number
  haltedAt: string | null
  haltedByName: string | null
  stallWarningSeconds: number | null
}

export const EMPTY_DISPATCH_PROGRESS: DispatchProgress = {
  secondsSinceProgress: null,
  pendingCount: 0,
  unconfirmedCount: 0,
  haltedAt: null,
  haltedByName: null,
  stallWarningSeconds: null
}

/** A queued / sending campaign that has made no recipient progress for the
 *  stall threshold. */
export const isDispatchStalled = (status: string | null, progress: DispatchProgress): boolean =>
  (status === 'queued' || status === 'sending') &&
  progress.secondsSinceProgress !== null &&
  progress.secondsSinceProgress >= (progress.stallWarningSeconds ?? DEFAULT_STALL_WARNING_SECONDS)

// Tag status vocabulary (docs/FRONTEND_DESIGN_PATTERNS.md): pending blue,
// done green, needs-attention gold, failure red; partially_failed keeps the
// Sent log's orange.
const STATUS_TAG_COLOR: Record<string, string> = {
  queued: 'blue',
  sending: 'blue',
  halted: 'gold',
  sent: 'green',
  partially_failed: 'orange',
  failed: 'red',
  cancelled: 'default'
}

/** "45 s", "3 min", "2 h 5 min". */
export const formatElapsed = (seconds: number): string => {
  if (seconds < 90) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} min` : `${hours} h`
}

const recipients = (n: number) => `${n} recipient${n === 1 ? '' : 's'}`

const UNCONFIRMED_ADVICE =
  'A dispatch run stopped before recording whether Postmark accepted them. Check Postmark & re-queue: ' +
  'recipients Postmark accepted are marked sent, the rest go back to the queue — nothing is sent until you '

// A mailbox campaign has nothing in Postmark: what left is in the sender's
// own mailbox. Its unconfirmed recipients also include a send the mailbox
// never answered.
const MAILBOX_UNCONFIRMED_ADVICE =
  'A dispatch run stopped before recording whether they were sent, or the mailbox did not answer. ' +
  "Check Sent Items & re-queue: recipients whose email is in the sender's mailbox are marked sent, " +
  'the rest go back to the queue — nothing is sent until you '

/** The wording of the check of unconfirmed recipients, by where the campaign
 *  was sent from: the form's button, its confirm modal and its toasts. */
export interface UnconfirmedCheckWording {
  button: string
  title: string
  /** "Each is looked up in <lookup>:" */
  lookup: string
  /** "<found> → marked sent" */
  found: string
  okText: string
  /** "<done> — n confirmed sent, n re-queued" */
  done: string
  failed: string
}

export const UNCONFIRMED_CHECK: { postmark: UnconfirmedCheckWording; mailbox: UnconfirmedCheckWording } = {
  postmark: {
    button: 'Check Postmark & re-queue',
    title: 'Check these recipients against Postmark?',
    lookup: "Postmark's message history for this campaign",
    found: 'accepted by Postmark',
    okText: 'Check Postmark',
    done: 'Postmark check done',
    failed: 'Failed to check the campaign against Postmark'
  },
  mailbox: {
    button: 'Check Sent Items & re-queue',
    title: "Check these recipients against the sender's Sent Items?",
    lookup: "the sender's mailbox, among the emails this campaign sent",
    found: "found in the sender's mailbox",
    okText: 'Check Sent Items',
    done: 'Sent Items check done',
    failed: "Failed to check the campaign against the sender's Sent Items"
  }
}

/** What sends the re-queued recipients once the check is done. A failed
 *  mailbox campaign has no Retry dispatch: Re-queue failed starts it. */
export const afterCheckAction = (status: string | null, viaMailbox: boolean): string =>
  status === 'halted' ? 'resume' : viaMailbox ? 'use Re-queue failed' : 'retry the dispatch'

/** A halted / failed campaign with recipients the check can resolve. */
export const hasUnconfirmedRecipients = (status: string | null, progress: DispatchProgress): boolean =>
  (status === 'halted' || status === 'failed' || status === 'partially_failed') && progress.unconfirmedCount > 0

interface DispatchStatusBannerProps {
  status: string
  sentCount: number
  failedCount: number
  recipientCount: number
  dispatchedAt: string | null
  completedAt: string | null
  autoRefreshing: boolean
  progress: DispatchProgress
  /** The campaign is sent from the sender's own mailbox (channel 'mailbox'),
   *  not through Postmark. */
  viaMailbox?: boolean
}

const DispatchStatusBanner = ({
  status,
  sentCount,
  failedCount,
  recipientCount,
  dispatchedAt,
  completedAt,
  autoRefreshing,
  progress,
  viaMailbox = false
}: DispatchStatusBannerProps) => {
  const stalled = isDispatchStalled(status, progress)
  const elapsed = formatElapsed(progress.secondsSinceProgress ?? 0)

  return (
    <div style={{ padding: '12px 24px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
      <Space size='middle' wrap>
        <Tag color={STATUS_TAG_COLOR[status] ?? 'default'} style={{ fontSize: 13, padding: '2px 10px' }}>
          {status.toUpperCase()}
        </Tag>
        <Text>
          <strong>{sentCount}</strong> sent / <strong>{failedCount}</strong> failed of{' '}
          <strong>{recipientCount}</strong>
        </Text>
        {dispatchedAt && (
          <Text type='secondary' style={{ fontSize: 12 }}>
            Queued at {new Date(dispatchedAt).toLocaleString()}
          </Text>
        )}
        {completedAt && (
          <Text type='secondary' style={{ fontSize: 12 }}>
            Completed at {new Date(completedAt).toLocaleString()}
          </Text>
        )}
        {autoRefreshing && (
          <Text type='secondary' style={{ fontSize: 12 }}>
            Auto-refreshing every 5 s
          </Text>
        )}
      </Space>

      {stalled && status === 'sending' && (
        <Alert
          type='error'
          showIcon
          style={{ marginTop: 12 }}
          message={`Dispatch stalled — no progress for ${elapsed}`}
          description={
            `${recipients(progress.pendingCount)} still waiting to be sent. The send may have been ` +
            'stopped by the platform. Halt the campaign to stop it before investigating — ' +
            'recipients who already received the email stay sent, and the recipients list shows ' +
            'the error for any that failed.'
          }
        />
      )}
      {stalled && status === 'queued' && (
        <Alert
          type='error'
          showIcon
          style={{ marginTop: 12 }}
          message={`Dispatch hasn't started — queued ${elapsed} ago`}
          description={
            `${recipients(progress.pendingCount)} waiting. The dispatch may not have received the ` +
            'request. Retry dispatch to start it.'
          }
        />
      )}
      {status === 'halted' && (
        <Alert
          type='warning'
          showIcon
          style={{ marginTop: 12 }}
          message={
            `Halted${progress.haltedByName ? ` by ${progress.haltedByName}` : ''}` +
            `${progress.haltedAt ? ` at ${new Date(progress.haltedAt).toLocaleString()}` : ''}` +
            ` — ${recipients(progress.pendingCount)} waiting`
          }
          description={
            progress.unconfirmedCount > 0
              ? viaMailbox
                ? `${recipients(progress.unconfirmedCount)} unconfirmed. ${MAILBOX_UNCONFIRMED_ADVICE}resume.`
                : `${recipients(progress.unconfirmedCount)} unconfirmed. ${UNCONFIRMED_ADVICE}resume.`
              : 'Nothing more is sent while the campaign is halted. Resume to send to the remaining recipients, or cancel the campaign to end it.'
          }
        />
      )}
      {(status === 'failed' || status === 'partially_failed') && progress.unconfirmedCount > 0 && (
        <Alert
          type='warning'
          showIcon
          style={{ marginTop: 12 }}
          message={`${recipients(progress.unconfirmedCount)} unconfirmed`}
          description={`${viaMailbox ? MAILBOX_UNCONFIRMED_ADVICE : UNCONFIRMED_ADVICE}${afterCheckAction(status, viaMailbox)}.`}
        />
      )}
    </div>
  )
}

export default DispatchStatusBanner
